import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useI18n } from '../i18n/i18nContext';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { packageAPI, giftCardAPI, userAPI, paymentAPI } from '../services/api';
import { useAuth0 } from '@auth0/auth0-react';
import { getTranslatedField } from '../utils/translationUtils';
import { usePromotion } from '../contexts/PromotionContext';
import { getErrorMessage } from '../utils/apiErrorHandler';
import { UserProfile, Package } from '@shared/types';
import { pushToDataLayer, clearEcommerce, formatPackageItem } from '../utils/dataLayer';
import { formatPrice } from '@shared/utils';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const CheckoutForm: React.FC<{
  packageDetails: Package;
  giftCardBalance: number;
  finalAmount: number;
  user: any;
  userProfile: UserProfile | null;
  appliedGiftCard: { code: string; balance: number; } | null;
  promotion: any;
  initialPackageIds: string[];
  initialSubIds: string[];
}> = ({ packageDetails, giftCardBalance, finalAmount, user, userProfile, appliedGiftCard, promotion, initialPackageIds, initialSubIds }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useI18n();
  const navigate = useLocalizedNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [isPollingOrder, setIsPollingOrder] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setName(userProfile.name || '');
      setEmail(userProfile.email || '');
    } else if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user, userProfile]);

  const waitForOrderCompletion = async (type: 'subscription' | 'package') => {
    if (!user && !userProfile) return;

    setIsPollingOrder(true);
    const maxAttempts = 10;
    const minDisplayTime = 2000;
    const pollStartTime = Date.now();
    let attempts = 0;

    const checkData = async () => {
      try {
        if (type === 'subscription') {
          const res = await userAPI.getSubscriptions();
          const resData = res.data as any;
          const list = Array.isArray(resData) ? resData : (resData?.data || []);
          return list.some((item: any) => !initialSubIds.includes(item.id));
        } else {
          const res = await userAPI.getUserPackages();
          const resData = res.data as any;
          const list = Array.isArray(resData) ? resData : (resData?.data || []);
          return list.some((item: any) => !initialPackageIds.includes(item.id));
        }
      } catch (e) {
        return false;
      }
    };

    while (attempts < maxAttempts) {
      const success = await checkData();
      if (success) break;
      await new Promise(r => setTimeout(r, 2000));
      attempts++;
    }

    const elapsed = Date.now() - pollStartTime;
    if (elapsed < minDisplayTime) {
      await new Promise(r => setTimeout(r, minDisplayTime - elapsed));
    }

    setIsPollingOrder(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setAttemptedSubmit(true);

    if (!stripe || !elements) {
      return;
    }

    if (name.trim() === '' || email.trim() === '') {
      return;
    }

    // GTM: Fire add_payment_info when user initiates payment (clicks Pay)
    if (packageDetails) {
      clearEcommerce();
      const category = packageDetails.type === 'subscription' ? 'subscription' : 'package';
      const itemType = packageDetails.type === 'subscription' ? 'Subscription' : 'Package';
      const couponValue = appliedGiftCard?.code || (promotion?.title ? (typeof promotion.title === 'string' ? promotion.title : promotion.title.en || Object.values(promotion.title)[0]) : undefined);

      pushToDataLayer('add_payment_info', {
        content: { category },
        ecommerce: {
          payment_type: 'pay_with_card',
          ...(couponValue && { coupon: couponValue }),
          items: [formatPackageItem(packageDetails, itemType)]
        },
        customer: {
          email: userProfile?.email || user?.email || '',
          phone: userProfile?.phoneNumber || user?.phone_number || ''
        }
      });
    }

    setIsProcessing(true);
    setError(null);

    try {
      const card = elements.getElement(CardNumberElement);
      if (!card) {
        throw new Error(t.checkout.cardElementError);
      }

      let paymentIntentResponse;
      if (packageDetails.type === 'subscription') {
        paymentIntentResponse = await paymentAPI.createSubscriptionIntent({
          subscriptionId: packageDetails.id,
          giftCardCode: appliedGiftCard?.code,
        });
      } else {
        paymentIntentResponse = await paymentAPI.createPaymentIntent({
          packageId: packageDetails.id,
          giftCardCode: appliedGiftCard?.code,
        });
      }

      const responseData = paymentIntentResponse.data as any;
      const { clientSecret, intentType, subscriptionId, requiresAction } = responseData;

      // Handle subscription without client secret (new flow)
      if (intentType === 'subscription' && !clientSecret && requiresAction) {
        // Create payment method first
        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: card,
          billing_details: {
            name: name,
            email: email,
          },
        });

        if (pmError) {
          setError(pmError.message || t.checkout.paymentError);
          setIsProcessing(false);
          return;
        }

        // Confirm subscription with the payment method
        const confirmResponse = await paymentAPI.confirmSubscription({
          subscriptionId: subscriptionId,
          paymentMethodId: paymentMethod?.id,
        });

        const confirmData = confirmResponse.data as any;
        if (confirmData.requiresAction && confirmData.clientSecret) {
          // 3D Secure confirmation required
          const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
            confirmData.clientSecret
          );

          if (confirmError) {
            throw new Error(confirmError.message || t.checkout.paymentError);
          }

          if (paymentIntent?.status === 'succeeded') {
            paymentAPI.createContractAfterPayment({
              paymentIntentId: paymentIntent.id,
              packageId: packageDetails.id,
            }).catch(err => console.error('Failed to create contract:', err));
            await waitForOrderCompletion('subscription');
            const subscriptionSearchParams = new URLSearchParams({
              payment_intent: paymentIntent.id,
              packageId: packageDetails.id
            });
            const coupon = appliedGiftCard?.code || (promotion?.title ? (typeof promotion.title === 'string' ? promotion.title : promotion.title.en || Object.values(promotion.title)[0]) : undefined);
            navigate(`/purchase-confirmation?${subscriptionSearchParams.toString()}`, {
              state: {
                packageName: packageDetails.name,
                amount: finalAmount,
                giftCardUsed: giftCardBalance > 0,
                ...(coupon && { coupon }),
                customerEmail: userProfile?.email || user?.email || '',
                customerPhone: userProfile?.phoneNumber || user?.phone_number || '',
              },
            });
          } else if (paymentIntent?.status === 'requires_payment_method') {
            throw new Error('Payment failed. Please try a different card.');
          }
        } else if (confirmData.success) {
          await waitForOrderCompletion('subscription');
          const subscriptionSuccessParams = new URLSearchParams({
            packageId: packageDetails.id
          });
          const coupon = appliedGiftCard?.code || (promotion?.title ? (typeof promotion.title === 'string' ? promotion.title : promotion.title.en || Object.values(promotion.title)[0]) : undefined);
          navigate(`/purchase-confirmation?${subscriptionSuccessParams.toString()}`, {
            state: {
              packageName: packageDetails.name,
              amount: finalAmount,
              giftCardUsed: giftCardBalance > 0,
              packageType: packageDetails.type,
              ...(coupon && { coupon }),
              customerEmail: userProfile?.email || user?.email || '',
              customerPhone: userProfile?.phoneNumber || user?.phone_number || '',
            },
          });
        } else {
          throw new Error(confirmData.error || confirmData.details || t.checkout.paymentError);
        }
      } else if (intentType === 'setup_intent' || intentType === 'setup_intent_for_future' || intentType === 'no_payment_required') {
        // Handle setup intent (for $0 invoices or trials) or no payment required
        if (intentType === 'no_payment_required' && !clientSecret) {
          await waitForOrderCompletion(packageDetails.type === 'subscription' ? 'subscription' : 'package');
          const noPaymentParams = new URLSearchParams({
            packageId: packageDetails.id
          });
          const coupon = appliedGiftCard?.code || (promotion?.title ? (typeof promotion.title === 'string' ? promotion.title : promotion.title.en || Object.values(promotion.title)[0]) : undefined);
          navigate(`/purchase-confirmation?${noPaymentParams.toString()}`, {
            state: {
              packageName: packageDetails.name,
              amount: finalAmount,
              giftCardUsed: giftCardBalance > 0,
              packageType: packageDetails.type,
              ...(coupon && { coupon }),
              customerEmail: userProfile?.email || user?.email || '',
              customerPhone: userProfile?.phoneNumber || user?.phone_number || '',
            },
          });
          return;
        }

        // Confirm setup intent for future payments
        const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: card,
            billing_details: {
              name: name,
              email: email,
            },
          },
        });

        if (stripeError) {
          throw new Error(stripeError.message || t.checkout.paymentError);
        }

        if (setupIntent?.status === 'succeeded') {
          await waitForOrderCompletion(packageDetails.type === 'subscription' ? 'subscription' : 'package');
          const setupIntentParams = new URLSearchParams({
            packageId: packageDetails.id
          });
          const coupon = appliedGiftCard?.code || (promotion?.title ? (typeof promotion.title === 'string' ? promotion.title : promotion.title.en || Object.values(promotion.title)[0]) : undefined);
          navigate(`/purchase-confirmation?${setupIntentParams.toString()}`, {
            state: {
              packageName: packageDetails.name,
              amount: finalAmount,
              giftCardUsed: giftCardBalance > 0,
              packageType: packageDetails.type,
              ...(coupon && { coupon }),
              customerEmail: userProfile?.email || user?.email || '',
              customerPhone: userProfile?.phoneNumber || user?.phone_number || '',
            },
          });
        }
      } else if (clientSecret) {
        // Handle payment intent (for regular payments)
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: card,
            billing_details: {
              name: name,
              email: email,
            },
          },
        });

        if (stripeError) {
          throw new Error(stripeError.message || t.checkout.paymentError);
        }

        if (paymentIntent?.status === 'succeeded') {
          paymentAPI.createContractAfterPayment({
            paymentIntentId: paymentIntent.id,
            packageId: packageDetails.id,
          }).catch(err => console.error('Failed to create contract:', err));
          await waitForOrderCompletion(packageDetails.type === 'subscription' ? 'subscription' : 'package');
          const searchParams = new URLSearchParams({
            payment_intent: paymentIntent.id,
            packageId: packageDetails.id
          });
          const coupon = appliedGiftCard?.code || (promotion?.title ? (typeof promotion.title === 'string' ? promotion.title : promotion.title.en || Object.values(promotion.title)[0]) : undefined);
          navigate(`/purchase-confirmation?${searchParams.toString()}`, {
            state: {
              packageName: packageDetails.name,
              amount: finalAmount,
              giftCardUsed: giftCardBalance > 0,
              packageType: packageDetails.type,
              ...(coupon && { coupon }),
              customerEmail: userProfile?.email || user?.email || '',
              customerPhone: userProfile?.phoneNumber || user?.phone_number || '',
            },
          });
        }
      } else {
        throw new Error('Invalid payment response from server');
      }
    } catch (err: any) {
      setError(getErrorMessage(err, t, t.checkout.paymentError));
      setIsProcessing(false);
    }
  };

  return (
    <>
      {isPollingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl text-center max-w-sm mx-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <h3 className="text-xl font-bold mb-2 text-gray-800">{t.checkout.processingOrder || 'Processing Order'}</h3>
            <p className="text-gray-600">{t.checkout.pleaseWait || 'Please wait while we confirm your purchase...'}</p>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="mb-4 space-y-2">
        <p className="text-sm text-gray-600 mb-2">{t.checkout.customerInfo}:</p>
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            {t.checkout.name} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`mt-1 block w-full border rounded-md shadow-sm p-2 focus:ring-green-500 focus:border-green-500 ${
              attemptedSubmit && name.trim() === '' ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
            required
          />
          {attemptedSubmit && name.trim() === '' && (
            <p className="mt-1 text-sm text-red-600">{t.booking.nameRequired || 'Name is required'}</p>
          )}
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            {t.checkout.email} <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`mt-1 block w-full border rounded-md shadow-sm p-2 focus:ring-green-500 focus:border-green-500 ${
              attemptedSubmit && email.trim() === '' ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
            required
          />
          {attemptedSubmit && email.trim() === '' && (
            <p className="mt-1 text-sm text-red-600">{t.booking.emailRequired || 'Email is required'}</p>
          )}
        </div>
      </div>

      {(finalAmount > 0 || (finalAmount === 0 && packageDetails.type === 'subscription')) ? (
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              {t.checkout.cardNumber || 'Card Number'}
            </label>
            <div className="border border-gray-300 rounded-md p-4">
              <CardNumberElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#32325d',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {t.checkout.expiryDate || 'Expiry Date'}
              </label>
              <div className="border border-gray-300 rounded-md p-4">
                <CardExpiryElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#32325d',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {t.checkout.cvc || 'CVC'}
              </label>
              <div className="border border-gray-300 rounded-md p-4">
                <CardCvcElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#32325d',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded">
          <p>{packageDetails.type === 'subscription'
            ? 'Your first payment is fully covered by the gift card! We still need your payment details for future charges.'
            : 'Your purchase is fully covered by the gift card!'}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <button
        type="submit"
        onClick={(e) => {
          if (!stripe || isProcessing || name.trim() === '' || email.trim() === '') {
            e.preventDefault();
            setAttemptedSubmit(true);
          }
        }}
        className={`w-full py-3 px-4 rounded-md transition-colors font-medium ${
          !stripe || isProcessing || name.trim() === '' || email.trim() === ''
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-green-600 text-white hover:bg-green-700'
        }`}
      >
        {isProcessing
          ? t.checkout.processing
          : finalAmount === 0
          ? t.checkout.completeOrder
          : t.checkout.payAmount.replace('{{amount}}', formatPrice(finalAmount))}
        </button>
      </form>
    </>
  );
};

const CheckoutPage: React.FC = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const { t, language } = useI18n();
  const { user, isAuthenticated } = useAuth0();
  const promotion = usePromotion();
  const [packageDetails, setPackageDetails] = useState<Package | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [giftCardCode, setGiftCardCode] = useState('');
  const [appliedGiftCard, setAppliedGiftCard] = useState<{
    code: string;
    balance: number;
  } | null>(null);
  const [giftCardError, setGiftCardError] = useState<string | null>(null);
  const [applyingGiftCard, setApplyingGiftCard] = useState(false);
  const [initialPackageIds, setInitialPackageIds] = useState<string[]>([]);
  const [initialSubIds, setInitialSubIds] = useState<string[]>([]);
  const [baselineLoaded, setBaselineLoaded] = useState(false);

  useDocumentTitle(t.checkout.title);

  useEffect(() => {
    fetchPackageDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserProfile();
      fetchBaseline();
    } else if (!isAuthenticated) {
      setBaselineLoaded(true);
    }
  }, [isAuthenticated, user]);

  const hasTrackedBeginCheckout = useRef(false);
  useEffect(() => {
    if (packageDetails && !hasTrackedBeginCheckout.current) {
      hasTrackedBeginCheckout.current = true;
      clearEcommerce();
      const category = packageDetails.type === 'subscription' ? 'subscription' : 'package';
      const itemType = packageDetails.type === 'subscription' ? 'Subscription' : 'Package';
      
      pushToDataLayer('begin_checkout', {
        content: { category },
        ecommerce: {
          items: [formatPackageItem(packageDetails, itemType)]
        }
      });
    }
  }, [packageDetails]);

  const fetchPackageDetails = async () => {
    try {
      const packagesResponse = await packageAPI.getAll();
      console.log('API response:', packagesResponse);
      
      const responseData = (packagesResponse.data as any)?.data || packagesResponse.data;
      const allPackages = Array.isArray(responseData?.packages) 
        ? responseData.packages 
        : Array.isArray(responseData) 
          ? responseData 
          : [];
      
      if (!Array.isArray(allPackages)) {
        throw new Error('Invalid packages data format');
      }
      
      const selectedPackage = allPackages.find((pkg: Package) => pkg.id === packageId);

      if (!selectedPackage) {
        throw new Error(t.checkout.packageNotFound);
      }

      setPackageDetails(selectedPackage);
    } catch (err: any) {
      setError(err.message || t.checkout.loadError);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await userAPI.getProfile();
      setUserProfile(response.data);
    } catch (err: any) {
      console.error('Failed to fetch user profile:', err);
    }
  };

  const fetchBaseline = async () => {
    try {
      const [pkgsRes, subsRes] = await Promise.all([
        userAPI.getUserPackages().catch(() => ({ data: [] })),
        userAPI.getSubscriptions().catch(() => ({ data: [] }))
      ]);

      const getIds = (res: any) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        return list.map((item: any) => item.id);
      };

      setInitialPackageIds(getIds(pkgsRes));
      setInitialSubIds(getIds(subsRes));
    } catch (err) {
      console.warn('Failed to fetch baseline for polling', err);
    } finally {
      setBaselineLoaded(true);
    }
  };

  const handleApplyGiftCard = async () => {
    if (!giftCardCode.trim()) {
      setGiftCardError(t.checkout.enterGiftCardCode);
      return;
    }

    setApplyingGiftCard(true);
    setGiftCardError(null);

    try {
      const response = await giftCardAPI.getBalance(giftCardCode);
      const balance = response.data.balance;

      if (balance <= 0) {
        throw new Error(t.checkout.giftCardNoBalance);
      }

      setAppliedGiftCard({
        code: giftCardCode,
        balance: balance,
      });
      setGiftCardCode('');
    } catch (err: any) {
      setGiftCardError(getErrorMessage(err, t, t.checkout.giftCardError));
    } finally {
      setApplyingGiftCard(false);
    }
  };

  const handleRemoveGiftCard = () => {
    setAppliedGiftCard(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">{t.common.loading}</div>
      </div>
    );
  }

  if (error || !packageDetails) {
    return (
      <div className="container mx-auto p-4 md:p-8 max-w-4xl">
        <div className="text-center py-10">
          <div className="text-red-600 mb-4">{error || t.checkout.loadError}</div>
          <button
            onClick={() => window.history.back()}
            className="text-blue-600 hover:underline"
          >
            {t.common.goBack}
          </button>
        </div>
      </div>
    );
  }

  const discountedPrice = packageDetails.price;
  
  let giftCardAmountToApply = 0;
  if (appliedGiftCard) {
    giftCardAmountToApply = Math.min(appliedGiftCard.balance, discountedPrice);
  }
  
  const finalAmount = Math.max(0, discountedPrice - giftCardAmountToApply);
  
  let promotionDiscountPercentage = 0;
  if (promotion && packageDetails) {
    if (packageDetails.type === 'subscription' && promotion.subscription_discount_pct) {
      promotionDiscountPercentage = promotion.subscription_discount_pct;
    } else if (packageDetails.type === 'bulk' && promotion.package_discount_pct) {
      promotionDiscountPercentage = promotion.package_discount_pct;
    }
  }

  return (
    <div className="container mx-auto p-4 md:p-8 max-w-4xl">
      <h1 className="text-3xl md:text-4xl font-bold mb-8 text-center text-gray-800">
        {t.checkout.title}
      </h1>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-bold mb-4">{t.checkout.orderSummary}</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg">
                {getTranslatedField(packageDetails, 'name', language)}
              </h3>
              <p className="text-gray-600">
                {packageDetails.type === 'subscription'
                  ? t.checkout.monthlySubscription
                  : t.checkout.bulkPackage}
              </p>
              <p className="text-gray-600">
                {packageDetails.sessions} {t.checkout.sessions}
              </p>
            </div>

            <div className="border-t pt-4">
              {packageDetails.originalPrice && packageDetails.originalPrice > packageDetails.price && promotionDiscountPercentage > 0 ? (
                <>
                  <div className="flex justify-between mb-2">
                    <span>{t.checkout.subtotal}</span>
                    <span>{formatPrice(packageDetails.originalPrice)}</span>
                  </div>

                  <div className="flex justify-between mb-2 text-green-600">
                    <span>{promotionDiscountPercentage}% {t.checkout.promotionDiscount || 'Promotion Discount'}</span>
                    <span>-{formatPrice(packageDetails.originalPrice - packageDetails.price)}</span>
                  </div>
                </>
              ) : null}
              
              {appliedGiftCard && (
                <div className="flex justify-between mb-2 text-green-600">
                  <span>{t.checkout.giftCardDiscount}</span>
                  <span>-{formatPrice(giftCardAmountToApply)}</span>
                </div>
              )}

              <div className="flex justify-between font-bold text-lg">
                <span>{t.checkout.total}</span>
                <span>{formatPrice(finalAmount)}</span>
              </div>
            </div>

            {!appliedGiftCard && (
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">{t.checkout.haveGiftCard}</h3>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={giftCardCode}
                    onChange={(e) => setGiftCardCode(e.target.value)}
                    placeholder={t.checkout.enterCode}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={applyingGiftCard}
                  />
                  <button
                    onClick={handleApplyGiftCard}
                    disabled={applyingGiftCard}
                    className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 transition-colors disabled:opacity-50"
                  >
                    {applyingGiftCard ? t.common.loading : t.checkout.apply}
                  </button>
                </div>
                {giftCardError && (
                  <p className="mt-2 text-sm text-red-600">{giftCardError}</p>
                )}
              </div>
            )}

            {appliedGiftCard && (
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-600">
                    {t.checkout.giftCardApplied}: {appliedGiftCard.code}
                  </span>
                  <button
                    onClick={handleRemoveGiftCard}
                    className="text-sm text-red-600 hover:underline"
                  >
                    {t.checkout.remove}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center mb-2">
            <svg className="w-5 h-5 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <h2 className="text-xl font-bold">{t.checkout.paymentDetails}</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">{t.checkout.securePaymentInfo}</p>

          <Elements stripe={stripePromise}>
            {baselineLoaded ? (
              <CheckoutForm
                packageDetails={packageDetails}
                giftCardBalance={giftCardAmountToApply}
                finalAmount={finalAmount}
                user={user}
                userProfile={userProfile}
                appliedGiftCard={appliedGiftCard}
                promotion={promotion}
                initialPackageIds={initialPackageIds}
                initialSubIds={initialSubIds}
              />
            ) : (
              <div className="flex justify-center items-center py-8">
                <div className="text-gray-500">{t.common.loading}</div>
              </div>
            )}
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;